// Explicit `.js` extensions are required. monaco-editor's exports map is
// `"./*": "./*"`, which resolves to a path with no extension, and TypeScript
// will not append one to a mapped export target.
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';

// Language *services* (as opposed to basic syntax highlighting) run on web
// workers. Vite emits each as its own chunk using the configured `base`, so
// importing them with `?worker` is what makes them resolve under the app root
// instead of at the domain root -- the failure mode this file exists to prevent.
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker.js?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker.js?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker.js?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker.js?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker.js?worker';

// Basic grammars are cheap and synchronous; they need no worker at all.
import 'monaco-editor/esm/vs/basic-languages/markdown/markdown.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/python/python.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/shell/shell.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/yaml/yaml.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/rust/rust.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/go/go.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/java/java.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/cpp/cpp.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/csharp/csharp.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/php/php.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/sql/sql.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/xml/xml.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/dart/dart.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/lua/lua.contribution.js';
import 'monaco-editor/esm/vs/basic-languages/ini/ini.contribution.js';

// Full language services, with their workers.
import 'monaco-editor/esm/vs/language/json/monaco.contribution.js';
import 'monaco-editor/esm/vs/language/css/monaco.contribution.js';
import 'monaco-editor/esm/vs/language/html/monaco.contribution.js';
import 'monaco-editor/esm/vs/language/typescript/monaco.contribution.js';

import { registerRhai } from './rhai.js';

/**
 * Tell Monaco how to obtain a worker for a language.
 *
 * The worker factory is bundled by Vite and instantiated as a same-origin
 * script, so the URL is correct under any base path without extra work. The
 * explicit mapping is still required: without it Monaco guesses from the
 * language id and picks a path relative to the document root, which 404s when
 * the app is not served from `/`.
 */
declare global {
  interface Window {
    MonacoEnvironment?: monaco.Environment;
  }
}

window.MonacoEnvironment = {
  getWorker(_moduleId: string, label: string): Worker {
    switch (label) {
      case 'json':
        return new jsonWorker();
      case 'css':
      case 'scss':
      case 'less':
        return new cssWorker();
      case 'html':
      case 'handlebars':
      case 'razor':
        return new htmlWorker();
      case 'typescript':
      case 'javascript':
        return new tsWorker();
      default:
        return new editorWorker();
    }
  },
};

export { monaco };
export default monaco;

registerRhai(monaco);

/** Dark theme tuned to sit alongside the app's own palette. */
monaco.editor.defineTheme('ai-studio-dark', {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },
    { token: 'keyword', foreground: '7aa2f7' },
    { token: 'string', foreground: '9ece6a' },
    { token: 'number', foreground: 'ff9e64' },
    { token: 'type', foreground: '2ac3de' },
    { token: 'function', foreground: 'c4a7ff' },
    { token: 'constant', foreground: 'ff9e64' },
    { token: 'operator', foreground: '89ddff' },
    { token: 'string.escape', foreground: 'f7c873' },
  ],
  colors: {
    'editor.background': '#14161b',
    'editor.foreground': '#e6e9f0',
    'editorLineNumber.foreground': '#3d424d',
    'editorLineNumber.activeForeground': '#8b93a7',
    'editor.selectionBackground': '#2b4a80',
    'editor.lineHighlightBackground': '#1a1d24',
    'editorCursor.foreground': '#5b8cff',
    'editorIndentGuide.background1': '#22262e',
    'editorGutter.background': '#14161b',
    'editorWidget.background': '#1c1f26',
    'editorSuggestWidget.background': '#1c1f26',
    'scrollbarSlider.background': '#2a324288',
    'scrollbarSlider.hoverBackground': '#3a456688',
  },
});

/** Map a filename to a Monaco language id. */
export function languageForPath(path: string): string {
  const name = path.split('/').pop() ?? path;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';

  switch (ext) {
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'js':
    case 'jsx':
    case 'mjs':
    case 'cjs':
      return 'javascript';
    case 'json':
      return 'json';
    case 'rhai':
      return 'rhai';
    case 'css':
      return 'css';
    case 'scss':
      return 'scss';
    case 'less':
      return 'less';
    case 'html':
    case 'htm':
      return 'html';
    case 'md':
    case 'markdown':
      return 'markdown';
    case 'py':
      return 'python';
    case 'sh':
    case 'bash':
    case 'zsh':
      return 'shell';
    case 'yml':
    case 'yaml':
      return 'yaml';
    case 'rs':
      return 'rust';
    case 'go':
      return 'go';
    case 'java':
      return 'java';
    case 'cpp':
    case 'cc':
    case 'cxx':
    case 'h':
    case 'hpp':
      return 'cpp';
    case 'cs':
      return 'csharp';
    case 'php':
      return 'php';
    case 'sql':
      return 'sql';
    case 'xml':
    case 'svg':
      return 'xml';
    case 'dart':
      return 'dart';
    case 'lua':
      return 'lua';
    case 'ini':
    case 'cfg':
    case 'toml':
      return 'ini';
    case 'bat':
    case 'cmd':
    case 'ps1':
      return 'ini';
    default:
      return 'plaintext';
  }
}